import { useEffect, useState } from 'react'

import { Flex, ModalBody, ModalFooter, ModalHeader } from '@chakra-ui/react'

import { Flex as DarkFlex } from '@hivewatch/dark'
import type { ColorScheme } from '@hivewatch/dark'

import { Button } from '@/components/ui'

import { GridModalHeader } from '../../components/GridModalHeader'
import { scrollbarStyle } from '../../styles/styles'
import { IncidentOptionI } from '../../types/types'
import { IncidentSearchPanel } from './IncidentSearchPanel'
import { IncidentVideoCountsPanel } from './IncidentVideoCountsPanel'
import { PresetIncidentCard } from './PresetIncidentCard'

interface IncidentSelectExpectedCountsStepIProps {
  colorScheme?: ColorScheme
  isRunTestDisabled?: boolean
  onBack: () => void
  onClose: () => void
  onRunTest: () => void
  peopleIn: number
  peopleOut: number
  /**
   * When provided, the left panel defaults to a "preset" view that shows the
   * pre-populated incident instead of the search table. The user can toggle
   * to the table via "Select New Incident" and back via "Use ${displayId}".
   */
  presetIncident?: IncidentOptionI | null
  selectedIncident: IncidentOptionI | null
  setSelectedIncident: (option: IncidentOptionI | null) => void
  setPeopleIn: (n: number) => void
  setPeopleOut: (n: number) => void
}

export const IncidentSelectExpectedCountsStep = ({
  colorScheme,
  isRunTestDisabled,
  onBack,
  onClose,
  onRunTest,
  peopleIn,
  peopleOut,
  presetIncident,
  selectedIncident,
  setSelectedIncident,
  setPeopleIn,
  setPeopleOut,
}: IncidentSelectExpectedCountsStepIProps) => {
  const [mode, setMode] = useState<'preset' | 'search'>(
    presetIncident ? 'preset' : 'search'
  )

  // When entering preset mode, ensure the preset is the selected incident so
  // the right-side panel (video + counts) and Run Test work immediately.
  useEffect(() => {
    if (
      mode === 'preset' &&
      presetIncident &&
      selectedIncident?.id !== presetIncident.id
    ) {
      setSelectedIncident(presetIncident)
    }
  }, [mode, presetIncident, selectedIncident?.id, setSelectedIncident])

  // Reset counts when a different incident is selected.
  useEffect(() => {
    setPeopleIn(0)
    setPeopleOut(0)
  }, [selectedIncident?.id, setPeopleIn, setPeopleOut])

  const handleSelectNewIncident = () => {
    setMode('search')
    // Clear the selected incident so the right-hand panel collapses back to
    // the empty "Select an incident to preview video" placeholder.
    setSelectedIncident(null)
  }

  const handleUsePreset = () => {
    if (!presetIncident) return
    setMode('preset')
    setSelectedIncident(presetIncident)
  }

  const leftPanel =
    mode === 'preset' && presetIncident ? (
      <PresetIncidentCard
        colorScheme={colorScheme}
        onSelectNewIncident={handleSelectNewIncident}
        presetIncident={presetIncident}
      />
    ) : (
      <IncidentSearchPanel
        colorScheme={colorScheme}
        onUsePreset={presetIncident ? handleUsePreset : undefined}
        presetIncident={presetIncident}
        selectedIncident={selectedIncident}
        setSelectedIncident={setSelectedIncident}
      />
    )

  const rightPanel = (
    <IncidentVideoCountsPanel
      colorScheme={colorScheme}
      peopleIn={peopleIn}
      peopleOut={peopleOut}
      selectedIncident={selectedIncident}
      setPeopleIn={setPeopleIn}
      setPeopleOut={setPeopleOut}
    />
  )

  if (colorScheme) {
    return (
      <DarkFlex direction='row' gap='24px' mt='16px'>
        <DarkFlex direction='column' flex='1' minWidth='0'>
          {leftPanel}
        </DarkFlex>
        <DarkFlex direction='column' flex='1' minWidth='0'>
          {rightPanel}
        </DarkFlex>
      </DarkFlex>
    )
  }

  const content = (
    <Flex direction='row' gap={6} mt={4}>
      {/* Left panel — preset summary or incident search table */}
      <Flex direction='column' flex={1} minW={0}>
        {leftPanel}
      </Flex>

      {/* Right panel — video + expected counts */}
      <Flex direction='column' flex={1} minW={0}>
        {rightPanel}
      </Flex>
    </Flex>
  )

  return (
    <>
      <ModalHeader p={0} w='100%'>
        <GridModalHeader
          subtitle='Select an incident and set expected IN/OUT counts to test grid accuracy'
          title='Select Incident & Set Expected Counts'
        />
      </ModalHeader>
      <ModalBody p={0} sx={scrollbarStyle}>
        {content}
      </ModalBody>
      <ModalFooter mt={6} p={0}>
        <Flex direction='row' gap={3} justifyContent='flex-end'>
          <Button mr={0} onClick={onBack} variant='secondary'>
            Back
          </Button>
          <Button mr={0} onClick={onClose} variant='secondary'>
            Cancel
          </Button>
          <Button
            isDisabled={!selectedIncident || isRunTestDisabled}
            onClick={onRunTest}
          >
            Run Test
          </Button>
        </Flex>
      </ModalFooter>
    </>
  )
}
